// Pricing Plans and Feature Configuration
// This file defines all plan limits and feature flags used throughout the application

export type PlanType = 'growth' | 'professional' | 'business' | 'enterprise';

export type DispatchBoardLevel = 'basic' | 'full';
export type RouteOptimizationLevel = 'none' | 'basic' | 'advanced' | 'ai';
export type EtaPredictionLevel = 'none' | 'basic' | 'ai';
export type SettlementsLevel = 'basic' | 'full' | 'custom';
export type IftaReportingLevel = 'manual' | 'semi-auto' | 'automated';
export type CarrierOnboardingLevel = 'none' | 'manual' | 'automated';
export type ApiAccessLevel = 'none' | 'readonly' | 'full';
export type SupportLevel = 'none' | 'business_hours' | '24_7' | '24_7_priority';

export interface PlanLimits {
  maxTrucks: number;
  maxTrailers: number;
  maxDrivers: number;
  maxLoadsPerMonth: number;
  maxCustomers: number;
  maxCarriers: number;
  maxLanes: number;
  maxDocumentStorageGB: number;
  maxSmsPerMonth: number;
  maxEmailPerMonth: number;
  maxGeofences: number;
  maxMultiStops: number;
  maxInvoiceTemplates: number;
  maxDocumentTemplates: number;
  maxMessageTemplates: number;
  maxLoadBoards: number;
  maxEldIntegrations: number;
  maxWebhooks: number;
  maxStandardReports: number;
  maxCustomReports: number;
  maxDashboardWidgets: number;
  maxOnboardingSessions: number;
  gpsUpdateIntervalSeconds: number;
  auditRetentionDays: number;
  documentRetentionYears: number;
  slaGuarantee: number;
}

export interface PlanFeatures {
  // Dispatch
  dispatchBoard: DispatchBoardLevel;
  dragDropDispatch: boolean;
  routeOptimization: RouteOptimizationLevel;
  etaPredictions: EtaPredictionLevel;

  // Driver
  driverApp: boolean;
  driverSettlements: SettlementsLevel;
  driverScorecards: boolean;
  driverRecruiting: boolean;
  driverPayStatementsBranded: boolean;

  // Messaging
  inAppMessaging: boolean;
  pushNotifications: boolean;
  broadcastMessages: boolean;
  customerPortalMessaging: boolean;

  // Financial
  batchInvoicing: boolean;
  quickbooksIntegration: boolean;
  factoringIntegration: boolean;
  collectionsManagement: boolean;
  marginAnalysis: boolean;
  iftaReporting: IftaReportingLevel;
  fuelCardIntegration: boolean;
  revenueAnalytics: 'basic' | 'full';

  // Compliance
  csaScoreMonitoring: boolean;
  fmcsaIntegration: boolean;
  insuranceCertTracking: boolean;
  complianceAlerts: 'email' | 'email_inapp' | 'all_channels';

  // Documents
  podCapture: boolean;
  ocrScanning: boolean;
  eSignatures: boolean;

  // Integrations
  loadBoardImport: boolean;
  ediSupport: boolean;
  apiAccess: ApiAccessLevel;
  customIntegrations: boolean;

  // Reporting
  scheduledReports: boolean;
  excelExport: boolean;
  biToolIntegration: boolean;
  predictiveAnalytics: boolean;

  // Customer Portal
  customerLoginPortal: boolean;
  selfServiceQuotes: boolean;
  brandedPortal: boolean;
  customerApiAccess: boolean;

  // Broker features
  carrierOnboarding: CarrierOnboardingLevel;
  carrierScorecards: boolean;
  carrierPayments: boolean;

  // Support
  chatSupport: SupportLevel;
  phoneSupport: SupportLevel;
  dedicatedCsm: boolean;
  customTraining: boolean;
}

export interface PlanConfig {
  id: PlanType;
  name: string;
  price: number;
  annualPrice: number;
  perTruckPrice?: number; // Additional cost per truck beyond base plan limit
  description: string;
  fleetSize: string;
  limits: PlanLimits;
  features: PlanFeatures;
}

// Use -1 to represent unlimited
const UNLIMITED = -1;

export const PLAN_CONFIGS: Record<PlanType, PlanConfig> = {
  growth: {
    id: 'growth',
    name: 'Growth',
    price: 149,
    annualPrice: 119,
    description: 'Perfect for owner-operators and small fleets',
    fleetSize: '1-10 trucks',
    limits: {
      maxTrucks: 10,
      maxTrailers: 15,
      maxDrivers: 10,
      maxLoadsPerMonth: 100,
      maxCustomers: 25,
      maxCarriers: 0,
      maxLanes: 0,
      maxDocumentStorageGB: 5,
      maxSmsPerMonth: 100,
      maxEmailPerMonth: 500,
      maxGeofences: 5,
      maxMultiStops: 2,
      maxInvoiceTemplates: 1,
      maxDocumentTemplates: 3,
      maxMessageTemplates: 5,
      maxLoadBoards: 1,
      maxEldIntegrations: 1,
      maxWebhooks: 0,
      maxStandardReports: 5,
      maxCustomReports: 0,
      maxDashboardWidgets: 4,
      maxOnboardingSessions: 1,
      gpsUpdateIntervalSeconds: 900, // 15 minutes
      auditRetentionDays: 30,
      documentRetentionYears: 1,
      slaGuarantee: 99.5,
    },
    features: {
      dispatchBoard: 'basic',
      dragDropDispatch: false,
      routeOptimization: 'none',
      etaPredictions: 'none',
      driverApp: false,
      driverSettlements: 'basic',
      driverScorecards: false,
      driverRecruiting: false,
      driverPayStatementsBranded: false,
      inAppMessaging: false,
      pushNotifications: false,
      broadcastMessages: false,
      customerPortalMessaging: false,
      batchInvoicing: false,
      quickbooksIntegration: false,
      factoringIntegration: false,
      collectionsManagement: false,
      marginAnalysis: false,
      iftaReporting: 'manual',
      fuelCardIntegration: false,
      revenueAnalytics: 'basic',
      csaScoreMonitoring: false,
      fmcsaIntegration: false,
      insuranceCertTracking: false,
      complianceAlerts: 'email',
      podCapture: false,
      ocrScanning: false,
      eSignatures: false,
      loadBoardImport: false,
      ediSupport: false,
      apiAccess: 'none',
      customIntegrations: false,
      scheduledReports: false,
      excelExport: false,
      biToolIntegration: false,
      predictiveAnalytics: false,
      customerLoginPortal: false,
      selfServiceQuotes: false,
      brandedPortal: false,
      customerApiAccess: false,
      carrierOnboarding: 'none',
      carrierScorecards: false,
      carrierPayments: false,
      chatSupport: 'business_hours',
      phoneSupport: 'none',
      dedicatedCsm: false,
      customTraining: false,
    },
  },

  professional: {
    id: 'professional',
    name: 'Professional',
    price: 349,
    annualPrice: 279,
    perTruckPrice: 15,
    description: 'For growing carriers and small brokers',
    fleetSize: '11-50 trucks',
    limits: {
      maxTrucks: 50,
      maxTrailers: 75,
      maxDrivers: 50,
      maxLoadsPerMonth: 500,
      maxCustomers: 150,
      maxCarriers: 50,
      maxLanes: 25,
      maxDocumentStorageGB: 25,
      maxSmsPerMonth: 500,
      maxEmailPerMonth: 2500,
      maxGeofences: 25,
      maxMultiStops: 5,
      maxInvoiceTemplates: 3,
      maxDocumentTemplates: 10,
      maxMessageTemplates: 25,
      maxLoadBoards: 3,
      maxEldIntegrations: 3,
      maxWebhooks: 5,
      maxStandardReports: 15,
      maxCustomReports: 3,
      maxDashboardWidgets: 10,
      maxOnboardingSessions: 3,
      gpsUpdateIntervalSeconds: 60, // 1 minute
      auditRetentionDays: 90,
      documentRetentionYears: 3,
      slaGuarantee: 99.9,
    },
    features: {
      dispatchBoard: 'full',
      dragDropDispatch: true,
      routeOptimization: 'basic',
      etaPredictions: 'basic',
      driverApp: true,
      driverSettlements: 'full',
      driverScorecards: false,
      driverRecruiting: false,
      driverPayStatementsBranded: false,
      inAppMessaging: true,
      pushNotifications: true,
      broadcastMessages: true,
      customerPortalMessaging: false,
      batchInvoicing: true,
      quickbooksIntegration: true,
      factoringIntegration: false,
      collectionsManagement: false,
      marginAnalysis: true,
      iftaReporting: 'semi-auto',
      fuelCardIntegration: false,
      revenueAnalytics: 'full',
      csaScoreMonitoring: false,
      fmcsaIntegration: false,
      insuranceCertTracking: true,
      complianceAlerts: 'email_inapp',
      podCapture: true,
      ocrScanning: false,
      eSignatures: true,
      loadBoardImport: true,
      ediSupport: false,
      apiAccess: 'readonly',
      customIntegrations: false,
      scheduledReports: true,
      excelExport: true,
      biToolIntegration: false,
      predictiveAnalytics: false,
      customerLoginPortal: false,
      selfServiceQuotes: false,
      brandedPortal: false,
      customerApiAccess: false,
      carrierOnboarding: 'manual',
      carrierScorecards: false,
      carrierPayments: false,
      chatSupport: 'business_hours',
      phoneSupport: 'business_hours',
      dedicatedCsm: false,
      customTraining: false,
    },
  },

  business: {
    id: 'business',
    name: 'Business',
    price: 749,
    annualPrice: 599,
    perTruckPrice: 12,
    description: 'For mid-size carriers and established brokers',
    fleetSize: '51-200 trucks',
    limits: {
      maxTrucks: 200,
      maxTrailers: 300,
      maxDrivers: 200,
      maxLoadsPerMonth: 2000,
      maxCustomers: 500,
      maxCarriers: 500,
      maxLanes: UNLIMITED,
      maxDocumentStorageGB: 100,
      maxSmsPerMonth: 2000,
      maxEmailPerMonth: 10000,
      maxGeofences: UNLIMITED,
      maxMultiStops: 10,
      maxInvoiceTemplates: UNLIMITED,
      maxDocumentTemplates: UNLIMITED,
      maxMessageTemplates: UNLIMITED,
      maxLoadBoards: UNLIMITED,
      maxEldIntegrations: UNLIMITED,
      maxWebhooks: UNLIMITED,
      maxStandardReports: UNLIMITED,
      maxCustomReports: UNLIMITED,
      maxDashboardWidgets: UNLIMITED,
      maxOnboardingSessions: UNLIMITED,
      gpsUpdateIntervalSeconds: 30, // 30 seconds
      auditRetentionDays: 365,
      documentRetentionYears: 7,
      slaGuarantee: 99.9,
    },
    features: {
      dispatchBoard: 'full',
      dragDropDispatch: true,
      routeOptimization: 'advanced',
      etaPredictions: 'ai',
      driverApp: true,
      driverSettlements: 'custom',
      driverScorecards: true,
      driverRecruiting: true,
      driverPayStatementsBranded: true,
      inAppMessaging: true,
      pushNotifications: true,
      broadcastMessages: true,
      customerPortalMessaging: true,
      batchInvoicing: true,
      quickbooksIntegration: true,
      factoringIntegration: true,
      collectionsManagement: true,
      marginAnalysis: true,
      iftaReporting: 'automated',
      fuelCardIntegration: true,
      revenueAnalytics: 'full',
      csaScoreMonitoring: true,
      fmcsaIntegration: true,
      insuranceCertTracking: true,
      complianceAlerts: 'all_channels',
      podCapture: true,
      ocrScanning: true,
      eSignatures: true,
      loadBoardImport: true,
      ediSupport: true,
      apiAccess: 'full',
      customIntegrations: false,
      scheduledReports: true,
      excelExport: true,
      biToolIntegration: true,
      predictiveAnalytics: false,
      customerLoginPortal: true,
      selfServiceQuotes: true,
      brandedPortal: true,
      customerApiAccess: false,
      carrierOnboarding: 'automated',
      carrierScorecards: true,
      carrierPayments: true,
      chatSupport: '24_7',
      phoneSupport: '24_7',
      dedicatedCsm: true,
      customTraining: false,
    },
  },

  enterprise: {
    id: 'enterprise',
    name: 'Enterprise',
    price: 0, // Contact sales
    annualPrice: 0,
    description: 'For large enterprises and 3PLs',
    fleetSize: '200+ trucks',
    limits: {
      maxTrucks: UNLIMITED,
      maxTrailers: UNLIMITED,
      maxDrivers: UNLIMITED,
      maxLoadsPerMonth: UNLIMITED,
      maxCustomers: UNLIMITED,
      maxCarriers: UNLIMITED,
      maxLanes: UNLIMITED,
      maxDocumentStorageGB: UNLIMITED,
      maxSmsPerMonth: UNLIMITED,
      maxEmailPerMonth: UNLIMITED,
      maxGeofences: UNLIMITED,
      maxMultiStops: UNLIMITED,
      maxInvoiceTemplates: UNLIMITED,
      maxDocumentTemplates: UNLIMITED,
      maxMessageTemplates: UNLIMITED,
      maxLoadBoards: UNLIMITED,
      maxEldIntegrations: UNLIMITED,
      maxWebhooks: UNLIMITED,
      maxStandardReports: UNLIMITED,
      maxCustomReports: UNLIMITED,
      maxDashboardWidgets: UNLIMITED,
      maxOnboardingSessions: UNLIMITED,
      gpsUpdateIntervalSeconds: 10, // 10 seconds
      auditRetentionDays: 2555, // 7 years
      documentRetentionYears: UNLIMITED,
      slaGuarantee: 99.99,
    },
    features: {
      dispatchBoard: 'full',
      dragDropDispatch: true,
      routeOptimization: 'ai',
      etaPredictions: 'ai',
      driverApp: true,
      driverSettlements: 'custom',
      driverScorecards: true,
      driverRecruiting: true,
      driverPayStatementsBranded: true,
      inAppMessaging: true,
      pushNotifications: true,
      broadcastMessages: true,
      customerPortalMessaging: true,
      batchInvoicing: true,
      quickbooksIntegration: true,
      factoringIntegration: true,
      collectionsManagement: true,
      marginAnalysis: true,
      iftaReporting: 'automated',
      fuelCardIntegration: true,
      revenueAnalytics: 'full',
      csaScoreMonitoring: true,
      fmcsaIntegration: true,
      insuranceCertTracking: true,
      complianceAlerts: 'all_channels',
      podCapture: true,
      ocrScanning: true,
      eSignatures: true,
      loadBoardImport: true,
      ediSupport: true,
      apiAccess: 'full',
      customIntegrations: true,
      scheduledReports: true,
      excelExport: true,
      biToolIntegration: true,
      predictiveAnalytics: true,
      customerLoginPortal: true,
      selfServiceQuotes: true,
      brandedPortal: true,
      customerApiAccess: true,
      carrierOnboarding: 'automated',
      carrierScorecards: true,
      carrierPayments: true,
      chatSupport: '24_7',
      phoneSupport: '24_7_priority',
      dedicatedCsm: true,
      customTraining: true,
    },
  },
};

// Helper functions

export function getPlanConfig(planType: PlanType): PlanConfig {
  // Fallback to growth if invalid plan type
  return PLAN_CONFIGS[planType] || PLAN_CONFIGS.growth;
}

export function isUnlimited(value: number): boolean {
  return value === UNLIMITED;
}

export function checkLimit(current: number, limit: number): {
  allowed: boolean;
  remaining: number;
  percentage: number;
  warning: boolean;
  critical: boolean;
} {
  if (isUnlimited(limit)) {
    return {
      allowed: true,
      remaining: UNLIMITED,
      percentage: 0,
      warning: false,
      critical: false,
    };
  }

  const remaining = limit - current;
  const percentage = (current / limit) * 100;

  return {
    allowed: current < limit,
    remaining: Math.max(0, remaining),
    percentage: Math.min(100, percentage),
    warning: percentage >= 80 && percentage < 95,
    critical: percentage >= 95,
  };
}

export function hasFeature(
  planType: PlanType,
  feature: keyof PlanFeatures
): boolean {
  const config = getPlanConfig(planType);
  if (!config || !config.features) {
    return false;
  }

  const value = config.features[feature];

  if (typeof value === 'boolean') {
    return value;
  }

  // For string enums, return true if not 'none'
  return value !== 'none';
}

export function getFeatureValue<K extends keyof PlanFeatures>(
  planType: PlanType,
  feature: K
): PlanFeatures[K] {
  const config = getPlanConfig(planType);
  if (!config || !config.features) {
    // Return a safe default - for boolean features, return false
    return false as PlanFeatures[K];
  }
  return config.features[feature];
}

export function getLimit<K extends keyof PlanLimits>(
  planType: PlanType,
  limit: K
): PlanLimits[K] {
  const config = getPlanConfig(planType);
  if (!config || !config.limits) {
    // Return 0 as safe default for limits
    return 0 as PlanLimits[K];
  }
  return config.limits[limit];
}

export function getUpgradePlan(currentPlan: PlanType): PlanType | null {
  if (!currentPlan) {
    return 'growth'; // Default next plan from starter
  }

  const order: PlanType[] = ['growth', 'professional', 'business', 'enterprise'];
  const currentIndex = order.indexOf(currentPlan);

  if (currentIndex === -1 || currentIndex >= order.length - 1) {
    return null;
  }

  return order[currentIndex + 1];
}

export function comparePlans(
  plan1: PlanType,
  plan2: PlanType
): -1 | 0 | 1 {
  const order: PlanType[] = ['growth', 'professional', 'business', 'enterprise'];
  const index1 = order.indexOf(plan1);
  const index2 = order.indexOf(plan2);

  if (index1 < index2) return -1;
  if (index1 > index2) return 1;
  return 0;
}

// Feature descriptions for UI
export const FEATURE_DESCRIPTIONS: Record<keyof PlanFeatures, string> = {
  dispatchBoard: 'Visual dispatch board for managing loads',
  dragDropDispatch: 'Drag and drop loads between status columns',
  routeOptimization: 'Optimize routes for efficiency',
  etaPredictions: 'Predict arrival times accurately',
  driverApp: 'Mobile app for drivers',
  driverSettlements: 'Calculate and manage driver pay',
  driverScorecards: 'Track driver performance metrics',
  driverRecruiting: 'Tools for recruiting new drivers',
  driverPayStatementsBranded: 'Custom branded pay statements',
  inAppMessaging: 'Send messages within the platform',
  pushNotifications: 'Real-time push notifications',
  broadcastMessages: 'Send messages to multiple recipients',
  customerPortalMessaging: 'Customer communication portal',
  batchInvoicing: 'Create multiple invoices at once',
  quickbooksIntegration: 'Sync with QuickBooks',
  factoringIntegration: 'Connect with factoring companies',
  collectionsManagement: 'Manage payment collections',
  marginAnalysis: 'Analyze profit margins on loads',
  iftaReporting: 'IFTA fuel tax reporting',
  fuelCardIntegration: 'Connect fuel card data',
  revenueAnalytics: 'Revenue tracking and analysis',
  csaScoreMonitoring: 'Monitor CSA safety scores',
  fmcsaIntegration: 'FMCSA data integration',
  insuranceCertTracking: 'Track insurance certificates',
  complianceAlerts: 'Alerts for compliance issues',
  podCapture: 'Capture proof of delivery',
  ocrScanning: 'Automatic document scanning',
  eSignatures: 'Electronic signatures',
  loadBoardImport: 'Import loads from load boards',
  ediSupport: 'EDI 204/214/210 support',
  apiAccess: 'API access for integrations',
  customIntegrations: 'Custom integration development',
  scheduledReports: 'Schedule automatic reports',
  excelExport: 'Export data to Excel',
  biToolIntegration: 'Connect to BI tools',
  predictiveAnalytics: 'AI-powered predictions',
  customerLoginPortal: 'Customer self-service portal',
  selfServiceQuotes: 'Customers can request quotes',
  brandedPortal: 'Custom branded customer portal',
  customerApiAccess: 'API access for customers',
  carrierOnboarding: 'Onboard carrier partners',
  carrierScorecards: 'Track carrier performance',
  carrierPayments: 'Process carrier payments',
  chatSupport: 'Live chat support',
  phoneSupport: 'Phone support',
  dedicatedCsm: 'Dedicated customer success manager',
  customTraining: 'Custom training sessions',
};
